import { useCallback, useEffect, useState } from 'react';
import {
  parseCaptainQueue,
  serialiseCaptainQueue,
  initialiseQueue,
  type CaptainQueueEntry,
} from '@4cm/core/rotatingCaptain';
import { supabase, isPlaceholderSupabase } from '../supabase';

interface UseCaptainQueueResult {
  queue: CaptainQueueEntry[];
  loading: boolean;
  /** Save a new queue value. Returns ok=false on failure with an error string. */
  saveQueue: (next: CaptainQueueEntry[]) => Promise<{ ok: true } | { ok: false; error: string }>;
  /** Initialise the queue from a roster + persist. No-op when one already exists. */
  enable: (playerIds: readonly string[]) => Promise<{ ok: true } | { ok: false; error: string }>;
  /** Wipe the queue. */
  disable: () => Promise<{ ok: true } | { ok: false; error: string }>;
}

/**
 * Read + write the team's rotating-captain queue. Wraps the
 * teams.rotating_captain_queue jsonb column with the parse/serialise
 * helpers from @4cm/core/rotatingCaptain.
 */
export function useCaptainQueue(teamId: string | null | undefined): UseCaptainQueueResult {
  const [queue, setQueue] = useState<CaptainQueueEntry[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!teamId) {
      setQueue([]);
      return;
    }
    if (isPlaceholderSupabase()) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data } = await supabase
        .from('teams')
        .select('rotating_captain_queue')
        .eq('id', teamId)
        .maybeSingle();
      if (cancelled) return;
      setQueue(parseCaptainQueue(data?.rotating_captain_queue));
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  const saveQueue = useCallback(async (next: CaptainQueueEntry[]) => {
    if (!teamId) return { ok: false as const, error: 'No active team.' };
    if (isPlaceholderSupabase()) return { ok: false as const, error: 'Connect Supabase to save the queue.' };
    const { error } = await supabase
      .from('teams')
      .update({ rotating_captain_queue: serialiseCaptainQueue(next) })
      .eq('id', teamId);
    if (error) return { ok: false as const, error: error.message };
    setQueue(next);
    return { ok: true as const };
  }, [teamId]);

  const enable = useCallback(async (playerIds: readonly string[]) => {
    return saveQueue(initialiseQueue(playerIds));
  }, [saveQueue]);

  const disable = useCallback(async () => saveQueue([]), [saveQueue]);

  return { queue, loading, saveQueue, enable, disable };
}
